import {
  prisma,
  type Shape,
  type Prisma,
  EntityType,
  SeatSectionStatus,
  ShapeType,
} from "client";
import { ACTIVE_SEAT_SECTION_DELETE_BLOCKED } from "./seat-section-rules";

/** Parent/child deletes still need a multi-doc txn — bound above Prisma's 5s default. */
const SHAPE_TREE_TX_TIMEOUT_MS = 15_000;

/** Reject delete when any shape hosts an ACTIVE SEATS_SECTION. */
const assertNoActiveSeatSectionsOnShapes = async (
  shapeIds: string[],
  client: Pick<typeof prisma, "entity"> = prisma
) => {
  if (shapeIds.length === 0) return;
  const entities = await client.entity.findMany({
    where: {
      shapeId: { in: shapeIds },
      type: EntityType.SEATS_SECTION,
      deletedAt: null,
    },
    select: { seatSection: true },
  });
  if (
    entities.some(
      (entity) => entity.seatSection?.status === SeatSectionStatus.ACTIVE
    )
  ) {
    throw new Error(ACTIVE_SEAT_SECTION_DELETE_BLOCKED);
  }
};

const BODY_SIZE_KEYS = [
  "width",
  "height",
  "radius",
  "widthMeters",
  "heightMeters",
] as const;

const polygonPointsChanged = (
  currentPosition: unknown,
  nextPosition: unknown
): boolean => {
  try {
    const current =
      typeof currentPosition === "string"
        ? JSON.parse(currentPosition)
        : currentPosition;
    const next =
      typeof nextPosition === "string"
        ? JSON.parse(nextPosition)
        : nextPosition;
    if (
      !current ||
      !next ||
      typeof current !== "object" ||
      typeof next !== "object"
    ) {
      return false;
    }
    return (
      JSON.stringify((current as { points?: unknown }).points) !==
      JSON.stringify((next as { points?: unknown }).points)
    );
  } catch {
    return false;
  }
};

/**
 * Get all shapes
 * @param userId - User ID for permission checking
 * @param payload - Optional Prisma findMany arguments
 * @returns Array of shapes
 */
export async function getAllShapes(
  userId: string,
  payload?: Prisma.ShapeFindManyArgs
) {
  try {
    return await prisma.shape.findMany(payload);
  } catch (error: any) {
    throw new Error(`Failed to get shapes: ${error.message}`);
  }
}

/**
 * Get shapes by floor plan ID
 * @param floorPlanId - Floor plan ID
 * @param payload - Optional Prisma findMany arguments
 * @returns Array of shapes
 */
export async function getShapesByFloorPlanId(
  floorPlanId: string,
  payload?: Prisma.ShapeFindManyArgs
) {
  try {
    return await prisma.shape.findMany({
      ...payload,
      where: {
        floorPlanId,
        OR: [
          {
            parentId: null,
          },
          {
            parentId: {
              isSet: false,
            },
          },
        ],
        ...(payload?.where ?? {}),
      },
    });
  } catch (error: any) {
    throw new Error(`Failed to get shapes: ${error.message}`);
  }
}

/**
 * Get a shape by ID
 * @param userId - User ID for permission checking
 * @param id - Shape ID
 * @param payload - Optional Prisma findUnique arguments
 * @returns Shape or null if not found
 */
export async function getShapeById(
  userId: string,
  id: string,
  payload?: Prisma.ShapeFindUniqueArgs
) {
  try {
    return await prisma.shape.findUnique({
      where: { id },
      ...payload,
    });
  } catch (error: any) {
    throw new Error(`Failed to get shape: ${error.message}`);
  }
}

/**
 * Create a new shape
 * @param payload - Shape data
 * @param userId - User ID for permission checking
 * @returns Created shape
 */
export async function createShape(
  payload: Prisma.ShapeCreateInput,
  userId: string
) {
  try {
    const shape = await prisma.shape.create({
      data: payload,
      include: { children: true, entity: true },
    });

    if (shape.entity) {
      return await prisma.shape.update({
        where: { id: shape.id },
        data: { entityId: shape.entity.id },
        include: { children: true, entity: true },
      });
    }

    return shape;
  } catch (error: any) {
    console.error("Shape creation failed:", error);
    throw new Error(`Failed to create shape: ${error.message}`);
  }
}

/**
 * Update an existing shape
 * @param userId - User ID for permission checking
 * @param id - Shape ID
 * @param payload - Update data
 * @returns Updated shape
 */
export async function updateShape(
  userId: string,
  id: string,
  payload: Prisma.ShapeUpdateInput
) {
  try {
    return await prisma.shape.update({
      where: { id },
      data: payload,
    });
  } catch (error: any) {
    console.error("Shape update failed:", error);
    throw new Error(`Failed to update shape: ${error.message}`);
  }
}

export type ShapePatchInput = {
  id: string;
  changes: {
    position?: Prisma.InputJsonValue;
    properties?: Record<string, unknown>;
    locked?: boolean;
    zIndex?: number;
    entityId?: string | null;
    type?: Shape["type"];
  };
};

/** Per-doc write result — partial success is OK (no multi-doc txn). */
export type BulkShapeWriteResult = {
  ok: true;
  ids: string[];
  failedIds: string[];
};

/**
 * Batch-apply minimal patches — one findMany + parallel per-doc updates.
 * No interactive $transaction: each Mongo update is already atomic.
 * Uses allSettled so one failure does not hide which siblings committed.
 */
export async function bulkPatchShapes(
  userId: string,
  patches: ShapePatchInput[]
): Promise<BulkShapeWriteResult> {
  if (patches.length === 0) return { ok: true, ids: [], failedIds: [] };

  const ids = patches.map((p) => p.id);
  const existing = await prisma.shape.findMany({
    where: { id: { in: ids } },
    select: {
      id: true,
      type: true,
      position: true,
      properties: true,
      entity: {
        select: {
          type: true,
          seatSection: true,
        },
      },
    },
  });
  const byId = new Map(existing.map((s) => [s.id, s]));

  const updates: { id: string; data: Prisma.ShapeUpdateInput }[] = [];
  const failedIds: string[] = [];
  const noOpIds: string[] = [];

  for (const patch of patches) {
    const current = byId.get(patch.id);
    if (!current) {
      failedIds.push(patch.id);
      continue;
    }

    const { changes } = patch;

    // ACTIVE seats section: reject body resize / outline edits (identity lock).
    // Move + rotate-only (position x/y, rotation) stay allowed.
    if (
      current.entity?.type === EntityType.SEATS_SECTION &&
      current.entity.seatSection?.status === SeatSectionStatus.ACTIVE
    ) {
      const props = (current.properties as Record<string, unknown>) ?? {};
      const nextProps = changes.properties ?? {};
      let sizeChanged = false;
      for (const key of BODY_SIZE_KEYS) {
        if (nextProps[key] !== undefined && nextProps[key] !== props[key]) {
          sizeChanged = true;
          break;
        }
      }
      const outlineChanged =
        current.type === ShapeType.LINE_POLYGON &&
        changes.position !== undefined &&
        polygonPointsChanged(current.position, changes.position);
      const shapeTypeChanged =
        changes.type !== undefined && changes.type !== current.type;

      if (sizeChanged || outlineChanged || shapeTypeChanged) {
        failedIds.push(patch.id);
        continue;
      }
    }

    const data: Prisma.ShapeUpdateInput = {};

    if (changes.position !== undefined) {
      data.position = changes.position;
    }
    if (changes.properties !== undefined) {
      data.properties = {
        ...((current.properties as Record<string, unknown>) ?? {}),
        ...changes.properties,
      } as Prisma.ShapeUpdateInput["properties"];
    }
    if (changes.locked !== undefined) data.locked = changes.locked;
    if (changes.zIndex !== undefined) data.zIndex = changes.zIndex;
    if (changes.type !== undefined) data.type = changes.type;
    if (changes.entityId !== undefined) {
      data.entityId = changes.entityId;
    }

    if (Object.keys(data).length === 0) {
      noOpIds.push(patch.id);
      continue;
    }

    updates.push({ id: patch.id, data });
  }

  const settled = await Promise.allSettled(
    updates.map(({ id, data }) =>
      prisma.shape.update({
        where: { id },
        data,
      })
    )
  );

  const succeededIds = [...noOpIds];
  for (let i = 0; i < settled.length; i++) {
    const id = updates[i].id;
    if (settled[i].status === "fulfilled") {
      succeededIds.push(id);
    } else {
      failedIds.push(id);
    }
  }

  return { ok: true, ids: succeededIds, failedIds };
}

/**
 * Delete a shape
 * @param userId - User ID for permission checking
 * @param id - Shape ID
 * @returns Deleted shape
 */
export async function deleteShape(userId: string, id: string) {
  try {
    // IMPORTANT: Collect all shape IDs BEFORE starting the transaction
    // This prevents MongoDB transaction timeout issues
    const leveledIds = await collectShapeIdsByLevel(id);

    // Flatten all IDs for entity deletion
    const allIds = leveledIds.flat();
    await assertNoActiveSeatSectionsOnShapes(allIds);

    // Cross-doc atomicity needed (parent + children). Bound timeout above Prisma's 5s default.
    return await prisma.$transaction(
      async (tx: Prisma.TransactionClient) => {
        await tx.entity.deleteMany({
          where: { shapeId: { in: allIds } },
        });

        for (let i = leveledIds.length - 1; i >= 0; i--) {
          const levelIds = leveledIds[i];

          if (levelIds.length > 0) {
            await tx.shape.deleteMany({
              where: { id: { in: levelIds } },
            });
          }
        }

        return {
          success: true,
          deletedCount: allIds.length,
          levels: leveledIds.length,
        };
      },
      { timeout: SHAPE_TREE_TX_TIMEOUT_MS }
    );
  } catch (error: any) {
    console.error("Shape deletion failed:", error);
    throw new Error(`Failed to delete shape: ${error.message}`);
  }
}

export type BulkCreateShapeInput = {
  id?: string;
  type: Shape["type"];
  position?: Prisma.InputJsonValue;
  properties?: Prisma.ShapeCreateInput["properties"];
  locked?: boolean;
  zIndex?: number;
  floorPlanId: string;
  venueId: string;
  createdById: string;
};

/**
 * Bulk create — one HTTP round-trip from the client (copy/paste many, undo delete-many).
 * Per-doc creates with allSettled — report which ids committed vs failed.
 */
export async function bulkCreateShapes(
  userId: string,
  shapes: BulkCreateShapeInput[]
): Promise<{ shapes: Shape[]; failedIds: string[] }> {
  if (shapes.length === 0) return { shapes: [], failedIds: [] };

  const settled = await Promise.allSettled(
    shapes.map((shape) =>
      prisma.shape.create({
        data: {
          ...(shape.id ? { id: shape.id } : {}),
          type: shape.type,
          position: shape.position ?? undefined,
          properties: shape.properties ?? {},
          locked: shape.locked ?? false,
          zIndex: shape.zIndex ?? 0,
          floorPlan: { connect: { id: shape.floorPlanId } },
          venue: { connect: { id: shape.venueId } },
          createdBy: { connect: { id: shape.createdById || userId } },
        },
      })
    )
  );

  const created: Shape[] = [];
  const failedIds: string[] = [];

  for (let i = 0; i < settled.length; i++) {
    const result = settled[i];
    const inputId = shapes[i].id;
    if (result.status === "fulfilled") {
      created.push(result.value);
    } else if (inputId) {
      failedIds.push(inputId);
    }
  }

  return { shapes: created, failedIds };
}

/**
 * Bulk delete — one round-trip for multi-select / select-all delete (and redo of undo-create).
 * Collects each root's descendants outside the transaction, then one delete transaction.
 */
export async function bulkDeleteShapes(userId: string, ids: string[]) {
  if (ids.length === 0) {
    return { success: true, deletedCount: 0 };
  }

  try {
    const uniqueRoots = [...new Set(ids)];
    const perRootLevels = await Promise.all(
      uniqueRoots.map((id) => collectShapeIdsByLevel(id))
    );

    // Merge levels across roots (deepest-first delete still safe with deleteMany)
    const allIds = [
      ...new Set(perRootLevels.flatMap((levels) => levels.flat())),
    ];
    const maxDepth = Math.max(0, ...perRootLevels.map((l) => l.length));
    const mergedLevels: string[][] = [];
    for (let depth = 0; depth < maxDepth; depth++) {
      const atDepth = [
        ...new Set(perRootLevels.flatMap((levels) => levels[depth] ?? [])),
      ];
      if (atDepth.length > 0) mergedLevels[depth] = atDepth;
    }

    await assertNoActiveSeatSectionsOnShapes(allIds);

    return await prisma.$transaction(
      async (tx: Prisma.TransactionClient) => {
        await tx.entity.deleteMany({
          where: { shapeId: { in: allIds } },
        });

        for (let i = mergedLevels.length - 1; i >= 0; i--) {
          const levelIds = mergedLevels[i];
          if (levelIds?.length) {
            await tx.shape.deleteMany({
              where: { id: { in: levelIds } },
            });
          }
        }

        return {
          success: true,
          deletedCount: allIds.length,
        };
      },
      { timeout: SHAPE_TREE_TX_TIMEOUT_MS }
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to bulk delete shapes: ${error.message}`);
    }
    throw error;
  }
}

/**
 * Iteratively collect all shape IDs grouped by their depth level
 * Returns an array where each element is an array of shape IDs at that level
 * Level 0 = root shape, Level 1 = direct children, Level 2 = grandchildren, etc.
 *
 * NOTE: This runs OUTSIDE the transaction to avoid MongoDB transaction timeout issues
 * Uses iterative approach instead of recursion for better MongoDB performance
 * @param id - Root shape ID to start deletion from
 */
async function collectShapeIdsByLevel(id: string): Promise<string[][]> {
  // Verify root shape exists
  const rootShape = await prisma.shape.findUnique({
    where: { id },
    select: { id: true },
  });

  if (!rootShape) {
    throw new Error(`Shape with id ${id} not found`);
  }

  const levels: string[][] = [[id]];
  let currentLevelIds = [id];
  let currentLevel = 0;

  // Iteratively fetch children level by level
  while (currentLevelIds.length > 0) {
    // Fetch all children for current level in a single query
    const children = await prisma.shape.findMany({
      where: {
        parentId: { in: currentLevelIds },
      },
      select: { id: true },
    });

    if (children.length === 0) {
      break; // No more children, we're done
    }

    // Extract child IDs and add to next level
    const childIds = children.map((child) => child.id);
    levels[currentLevel + 1] = childIds;

    // Move to next level
    currentLevelIds = childIds;
    currentLevel++;
  }

  return levels;
}
